import type { ResultRecord, Timeframe } from '@dynatrace-sdk/client-query';

import { isPotentialDateValue } from '../../core/types/type-guards.js';
import { convertToNumberIfPureNumber } from '../../core/utils/string-utils.js';
import type { FieldEntry } from '../getFieldsArrayForRecordIndex.js';

/** Tries to find a time value for the given Grail record */
function findTimeValue(
  record: ResultRecord,
  fieldsArray: FieldEntry[],
): string | Timeframe {
  const timeDimension = fieldsArray.find(
    ({ type }) => type === 'timeframe' || type === 'timestamp',
  );

  if (
    timeDimension === undefined ||
    timeDimension.name === undefined ||
    timeDimension.name === null
  ) {
    throw Error(
      'Could not find "timeframe" or "timestamp" on the fields of the record',
    );
  } else {
    if (record === undefined || record === null) {
      throw Error(`No values found in record ${record}`);
    }

    const timeValue = record[timeDimension.name];

    if (!timeValue) {
      throw Error(`Could not find a value for field "${timeDimension.name}"`);
    }

    return timeValue as string | Timeframe;
  }
}

/**
 * Extracts a Date tuple for start and end dates.
 * @param record - The input record from the query
 * @param fieldsArray - Field entries
 */
export function extractStartEndDates(
  record: ResultRecord,
  fieldsArray: FieldEntry[],
): [Date, Date | undefined] {
  const timeValue = findTimeValue(record, fieldsArray);

  if (isPotentialDateValue(timeValue)) {
    const time = convertToNumberIfPureNumber(timeValue);
    return [new Date(time), undefined];
  } else if (timeValue.start && timeValue.end) {
    // new Date() either works with milliseconds (type number) or a
    // valid ISO string but not with the milliseconds as a string.
    return [new Date(timeValue.start), new Date(timeValue.end)];
  }

  throw Error('It was not possible to extract a start and/or end date');
}
